// import { METHODS } from '';
import {
  API_ROUTES,
  API_ROUTES_FOR_ACTIVE_MEMBER,
  API_ROUTES_FOR_BINDER,
  API_ROUTES_FOR_BROKER,
  API_ROUTES_FOR_CA,
} from '../constants';
import { HttpResponsePromise } from '../models';
import utils, { IActiveMemberAPIHeaders } from '../utils';
import {
  IApplicantBinderDetailsResponse,
  IBinderPayNowLoginResponse,
  IValidateActiveMemberResponse,
  IValidateMemberResponse,
  IHPSRedirectionParams,
  IActiveUserEmailResponse,
  IBrokerValidateMemberResponse,
} from './../../store/interface';

interface IValidateMemberDetails {
  memberId: string;
  zipCode: string;
  dateOfBirth: string;
}

interface IBrokerValidateMemberDetails extends IValidateMemberDetails {
  brokerName: string;
  brokerId: string;
  emailId: string;
}

interface IValidateActiveMemberHeaders extends IActiveMemberAPIHeaders {
  stateCode: string;
}

interface IBinderPayNowLogin {
  applicationId: string;
  dateOfBirth?: string | undefined;
  exchangeIndicator: string;
  memberAutopayEmail?: string | undefined;
  memberStateCode: string;
  memberZipCode?: number | undefined;
  monthlyBill?: number | undefined;
  paymentAmount: number;
  redirectUri: string;
  sourceSystem: string;
}

export interface IActiveMemberEmailHeaders extends IActiveMemberAPIHeaders {
  MPP_ID: number | undefined;
  STATE_CODE: string;
  MID: string;
  MEMBER_ID: string;
  BROKER_ID?: string;
}

export interface IBinderPayLaterLogin {
  billingId: string | null;
  dateOfBirth: string;
  last4OfSsn: string | null;
  zipCode: string;
}

export interface IDeclineConversionHeaders extends IActiveMemberAPIHeaders {
  MPP_Id?: string;
  STATE_CODE?: string;
}

export interface IDeclineConversion {
  dob: string;
  memberId: string | null | undefined;
  mid: string;
  username: string | undefined;
  zipCode: string;
}

interface IUpdateAutopayReminderHeaders extends IActiveMemberAPIHeaders {
  MPP_ID: number;
  STATE_CODE: string;
  MEMBER_ID: string;
}

export interface IUpdateAutopayReminderPayload {
  autoPayAction: string;
}

interface IValidateActiveMemberPayload {
  mid: string;
  username: string;
  policyId?: string;
}

interface IMemberReturnProps {
  validateMember: (
    value: IValidateMemberDetails,
    stateCode: string,
  ) => HttpResponsePromise<IValidateMemberResponse>;
  validateActiveMember: (
    data: IValidateActiveMemberPayload,
    headers: IValidateActiveMemberHeaders,
  ) => HttpResponsePromise<IValidateActiveMemberResponse>;
  validateCaMember: (
    data: IValidateActiveMemberPayload,
    headers: IValidateActiveMemberHeaders,
  ) => HttpResponsePromise<IValidateActiveMemberResponse>;
  binderPayNowLogin: (
    data: IBinderPayNowLogin,
  ) => HttpResponsePromise<IBinderPayNowLoginResponse>;
  binderPayLaterLogin: (
    data: IBinderPayLaterLogin,
  ) => HttpResponsePromise<IApplicantBinderDetailsResponse>;
  declineConversion: (
    data: IDeclineConversion,
    headers: IDeclineConversionHeaders,
  ) => HttpResponsePromise<IHPSRedirectionParams>;
  getEmailForActiveUser: (
    headers: IActiveMemberEmailHeaders,
  ) => HttpResponsePromise<IActiveUserEmailResponse>;
  getEmailForBroker: (
    headers: IActiveMemberEmailHeaders,
  ) => HttpResponsePromise<IActiveUserEmailResponse>;
  updateAutopayReminder: (
    data: IUpdateAutopayReminderPayload,
    headers: IUpdateAutopayReminderHeaders,
  ) => HttpResponsePromise<string>;
  validateBrokerPortalMember: (
    data: IBrokerValidateMemberDetails,
    stateCode: string,
  ) => HttpResponsePromise<IBrokerValidateMemberResponse>;
}

const member = (): IMemberReturnProps => {
  const { fetch } = utils;
  return {
    validateMember: (data: IValidateMemberDetails, STATE_CODE: string) =>
      fetch(API_ROUTES.member.validateMember, {
        method: 'POST',
        data,
        headers: { 
          STATE_CODE, 
          MEMBER_ID: data.memberId,
        },
      }),
    validateActiveMember: (
      data: IValidateActiveMemberPayload,
      headers: IValidateActiveMemberHeaders,
    ) => {
      return fetch(API_ROUTES_FOR_ACTIVE_MEMBER.member.validateMember, {
        method: 'POST',
        data,
        headers,
      });
    },
    validateCaMember: (
      data: IValidateActiveMemberPayload,
      headers: IValidateActiveMemberHeaders,
    ) => {
      return fetch(API_ROUTES_FOR_CA.member.validateMember, {
        method: 'POST',
        data,
        headers,
      });
    },
    binderPayNowLogin: (data: IBinderPayNowLogin) => {
      return fetch(API_ROUTES_FOR_BINDER.member.binderPayNowLogin, {
        method: 'POST',
        data,
      });
    },
    binderPayLaterLogin: (data: IBinderPayLaterLogin) => {
      return fetch(API_ROUTES_FOR_BINDER.member.binderPayLaterLogin, {
        method: 'POST',
        data,
      });
    },
    declineConversion: (
      data: IDeclineConversion,
      headers: IDeclineConversionHeaders,
    ) => {
      return fetch(API_ROUTES_FOR_ACTIVE_MEMBER.member.declineConversion, {
        method: 'POST',
        data,
        headers,
      });
    },
    getEmailForActiveUser: (headers: IActiveMemberEmailHeaders) => {
      return fetch(API_ROUTES_FOR_ACTIVE_MEMBER.member.getEmail, {
        method: 'GET',
        headers,
      });
    },
    getEmailForBroker: (headers: IActiveMemberEmailHeaders) => {
      return fetch(API_ROUTES_FOR_BROKER.member.getEmail, {
        method: 'GET',
        headers,
      });
    },
    updateAutopayReminder: (
      data: IUpdateAutopayReminderPayload,
      headers: IUpdateAutopayReminderHeaders,
    ) => {
      return fetch(API_ROUTES_FOR_ACTIVE_MEMBER.member.autopayReminder, {
        method: 'PUT',
        data,
        headers,
      });
    },
    validateBrokerPortalMember: (
      data: IBrokerValidateMemberDetails,
      STATE_CODE: string,
    ) =>
      fetch(API_ROUTES_FOR_BROKER.member.validateBrokerPortalMember, {
        method: 'POST',
        data,
        headers: { STATE_CODE, MEMBER_ID: data.memberId },
      }),
  };
};

export default member;
